<template>
  <div>
    <v-progress-linear
      v-if="loading || saving"
      style="padding:0;margin:0;position:absolute;top:0;left:0;right:0;"
      :indeterminate="true"
    />
    <v-expand-transition>
      <div
        class="contact-header"
        v-if="!loading || saving"
      >
        <div
          class="contact-header-container"
        >
          <div>
            <div style="display:flex;vertical-align: middle;align-items: center;">
              <v-icon
                size="48"
              >
                person
              </v-icon>
              <div
                style="padding-left: 10px;"
              >
                <div
                  style="font-size:20px;"
                >
                  {{contact.full_name}}
                </div>
                <div v-if="contact.cuid">
                  <b>Unique ID:</b> {{contact.cuid}}
                </div>
              </div>
              <div
                style="margin-left: 20px;"
              >
                <div
                  v-if="contact.email"
                >
                  <v-icon
                    size="20"
                    style="padding-right: 5px;"
                  >
                    mdi-at
                  </v-icon>
                  {{contact.email}}
                </div>
                <div
                  v-if="contact.phone"
                >
                  <v-icon
                    size="20"
                    style="padding-right: 5px;"
                  >
                    mdi-phone
                  </v-icon>
                  <a :href="'tel://'+contact.phone" style="text-decoration: none"> {{contact.phone}}</a><br/>
                </div>
              </div>
              <div
                style="margin-left: 15px;"
              >
                <v-chip
                  v-if="!contact.is_active"
                  color="red"
                  dark
                  label
                  small
                >
                  Disabled
                </v-chip>
                <v-chip
                  v-if="showBadEmail"
                  color="warning"
                  dark
                  label
                  small
                >
                  Bad E-mail
                </v-chip>
                <v-chip
                  v-if="contact.mobile_device_count > 0 "
                  color="success"
                  dark
                  label
                  small
                >
                  App Installed
                </v-chip>
                <v-chip
                  v-if="contact.phone && !contact.notify_by_phone && domainInfo.text_enabled"
                  color="warning"
                  dark
                  label
                  small
                >
                  SMS Not Subscribed
                </v-chip>
                <v-chip
                  v-if="contact.phone && contact.notify_by_phone && domainInfo.text_enabled"
                  color="info"
                  dark
                  label
                  small
                >
                  SMS Subscribed
                </v-chip>
                <v-chip
                  v-if="contact.email && !contact.notify_by_email"
                  color="warning"
                  dark
                  label
                  small
                >
                  Email Not Subscribed
                </v-chip>
                <v-chip
                  v-if="contact.email && contact.notify_by_email"
                  color="info"
                  dark
                  label
                  small
                >
                  Email Subscribed
                </v-chip>
              </div>
              <div style="text-align:right;flex-grow: 1;">
                <v-btn
                  v-if="checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
                  flat
                  small
                  :disabled="saving"
                  color="secondary"
                  @click="editing=true"
                >
                  <v-icon
                    left
                    color="secondary"
                  >
                    edit
                  </v-icon>
                  Edit
                </v-btn>
                <v-btn
                  v-if="contact.phone && !contact.notify_by_phone && checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
                  color="secondary"
                  :disabled="saving"
                  small
                  flat
                  @click="sendSMSRequest = true"
                >
                  <v-icon
                    left
                    color="secondary"
                  >
                  mdi-message-processing
                  </v-icon>
                  Send SMS Request
                </v-btn>
                <v-btn
                  v-if="canSendMagic && checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
                  color="secondary"
                  :disabled="saving"
                  flat
                  small
                  @click="getLoginLink = true"
                >
                  <v-icon
                    left
                    color="secondary"
                  >
                  mdi-key-link
                  </v-icon>
                  Get Login
                </v-btn>
                <v-btn
                  v-if="contact.is_active && checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
                  :loading="saving"
                  :disabled="saving"
                  small
                  flat
                  @click="toggleContactActive()"
                >
                  Disable Contact
                </v-btn>
                <v-btn
                  v-else-if="checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
                  color="primary"
                  :loading="saving"
                  :disabled="saving"
                  small
                  dark
                  @click="toggleContactActive()"
                >
                  Enable Contact
                </v-btn>
              </div>
            </div>
          </div>
          <div>
            <v-combobox
              v-model.trim="contact.tags"
              :items="tagResults"
              :search-input.sync="tagSearch"
              :disabled="saving"
              label="Tags"
              multiple
              item-text="tag"
              item-value="tag"
              :return-object="false"
              :readonly="!checkPermission('contacts', PERMISSIONS.CAN_EDIT)"
              flat
              solo
              auto-select-first
              small-chips
              hide-details
              hide-selected
              @input="updateTags"
              style="margin-top: 10px;"
            >
              <template v-slot:no-data>
                <v-list-tile
                  v-if="tagSearch && tagSearch.length > 0 && !tagSearching"
                >
                  <div>
                    <div>
                      Press
                      <v-chip
                        :color="`#262626`"
                        label
                        small
                        dark
                      >
                        ENTER
                      </v-chip>
                      <span class="subheading"> to create</span>
                      <v-chip
                        :color="`lighten-3`"
                        label
                        small
                        :outline="tagSearch.includes(':')"
                      >
                        {{ tagSearch }}
                      </v-chip>
                    </div>
                    <div
                      v-if="tagSearch.includes(':')"
                      style="font-size: 12px;text-emphasis: italic;color: #666;margin-bottom: 10px;"
                    >
                      Tags with a colon (:) will be created as a tag filter, and will create an additional header in Contact reports.
                    </div>
                  </div>
                </v-list-tile>
                <v-list-tile
                  v-else-if="tagSearching && tagSearch && tagSearch.length > 0"
                >
                  <span
                    class="subheading"
                  >
                    Searching for 
                    <v-chip
                      :color="`lighten-3`"
                      label
                      small
                    >
                      {{ tagSearch }}
                    </v-chip>
                  </span>
                  <v-progress-circular
                    indeterminate
                    size="20"
                    width="2"
                    style="margin-left: 15px;"
                  />
                </v-list-tile>
              </template>
              <template v-slot:selection="{ item, parent, selected }">
                <v-chip
                  label
                  small
                  :outline="item.includes(':')"
                >
                  {{ item }}
                  <v-icon
                    small
                    @click="parent.selectItem(item)"
                  >close</v-icon>
                </v-chip>
              </template>
              <template v-slot:prepend-inner>
                <v-icon
                  style="margin-left: 5px;"
                >
                  mdi-tag-multiple
                </v-icon>
              </template>
            </v-combobox>
            <v-alert
              :value="(contact.id && !contact.is_active)"
              type="error"
            >
              This contact is disabled. Enable the contact to allow the contact to log in, receive messages, and complete forms in {{ app_name }}.
            </v-alert>
            <v-alert
              :value="(contact.suspended)"
              type="error"
            >
              {{ bouncedErrorMessage }}
            </v-alert>
            <v-alert
              :value="(contact.id && contact.is_active && !contact.suspended && contact.bounces > 0)"
              type="warning"
            >
              {{ bouncedErrorMessage }}
            </v-alert>
            <v-alert
              :value="(contact.id && contact.is_active && !contact.suspended && contact.sms_bounce)"
              type="error"
            >
              SMS messages to <pre style="display: inline-block;">{{ contact.phone }}</pre> have bounced. The contact will not receive SMS messages until the phone number is updated or the contact resubscribes to SMS messages.<br />The Contact can resubscribe by texting "START", "YES", or "Y" to the phone number <b>{{ domainInfo.text_number }}</b>.
            </v-alert>
            <div
              style="padding-top: 5px;font-size: 12px;"
            >
              <i>Last modified by <span style="color:black;">{{contact.updated_by_name}}</span> at {{ convertToLocal(contact.updated_at) }}</i>
              <span
                v-if="contact.last_token_request"
              > - <b>Last Login Attempt:</b>  {{ contact.last_token_request ? convertToLocal(contact.last_token_request) : '' }}</span>
            </div>
          </div>
        </div>
      </div>
    </v-expand-transition>
    <v-expand-transition>
      <v-card
        v-if="contact.id"
      >
        <v-layout>
          <v-flex
            md7
            sm12
            style="border-right: 1px solid rgba(0,0,0,.12)"
          >
            <v-expand-transition>
              <v-tabs
                v-model="tabs"
                style="max-height: 100%;"
                slider-color="primary"
                color="transparent"
                class="hidden-md-and-up"
              >
                <v-tab
                  key="cases"
                >
                  Cases
                </v-tab>
                <v-tab
                  v-if="checkPermission('contacts_files', PERMISSIONS.CAN_VIEW)"
                  key="files"
                >
                  <v-badge
                    right
                    color="red"
                  >
                    <template
                      v-if="unreadCount(contact.uploads) > 0"
                      v-slot:badge
                    >
                      <span>{{unreadCount(contact.uploads)}}</span>
                    </template>
                    Files
                  </v-badge>
                </v-tab>
                <v-tab
                  v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
                  key="contactlog"
                >
                  Contact Log
                </v-tab>
              </v-tabs>
            </v-expand-transition>
            <v-fade-transition>
              <v-tabs-items
                v-model="tabs"
                style="height: 100vh;max-height:calc(100vh - 241px);background:white;overflow-y:scroll;overflow-x:hidden;"
              >
                <v-tab-item
                  key="cases"
                >
                  <v-fade-transition>
                    <CaseList
                      v-if="!this.$route.params.case"
                      :contact="contact.id"
                      style="padding: 15px;"
                      :softrouting="true"
                    >
                      <v-btn
                        v-if="!this.$route.params.case && checkPermission('cases', PERMISSIONS.CAN_CREATE)"
                        :to="{name: 'NewCase', params: {contact: this.contact.id}}"
                        dark
                        round
                        color="green"
                      >
                        <v-icon dark left>add</v-icon>
                        New Case
                      </v-btn>
                    </CaseList>
                  </v-fade-transition>
                  <v-fade-transition>
                    <ContactCase
                      v-if="this.$route.params.case"
                      :selected="this.$route.params.case"
                      @back="$router.push({ name: 'Contact', params: {id: $route.params.id}})"
                      style="height: 100vh;max-height:calc(100vh - 241px);background:white;overflow-y:auto;overflow-x:hidden;"
                    />
                  </v-fade-transition>
                </v-tab-item>
                <v-tab-item
                  v-if="checkPermission('contacts_files', PERMISSIONS.CAN_VIEW)"
                  key="files"
                >
                  <ContactFiles
                    :contact.sync="contact"
                    :loading.sync="loading"
                    @reload="fetch()"
                  />
                </v-tab-item>
                <v-tab-item
                  v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
                  class="hidden-md-and-up"
                  key="contactlog"
                  style="height: 100vh;max-height:calc(100vh - 241px);background:white;overflow:hidden;"
                >
                  <ContactMessages
                    v-if="$vuetify.breakpoint.smAndDown && !smsRequestSending"
                    :contact="contact"
                  />
                </v-tab-item>
              </v-tabs-items>
            </v-fade-transition>
          </v-flex>
          <v-flex
            v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
            class="hidden-sm-and-down"
            md5
          >
            <v-tabs
              v-model="rightTabs"
              style="max-height: 100%;"
              slider-color="primary"
              color="transparent"
              class="hidden-sm-and-down"
            >
              <v-tab
                v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
                key="cases"
              >
                Contact Log
              </v-tab>
              <v-tab
                v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
                key="files"
              >
                <v-badge
                  right
                  color="red"
                >
                  <template
                    v-if="unreadCount(contact.uploads) > 0"
                    v-slot:badge
                  >
                    <span>{{unreadCount(contact.uploads)}}</span>
                  </template>
                  Files
                </v-badge>
              </v-tab>
              <v-spacer/>
              <v-btn
                @click="downloadCSV"
                :disabled="downloadingCSV"
                flat
                icon
                title="Download messages as a CSV"
              >
                <v-icon>
                  mdi-table-arrow-down
                </v-icon>
              </v-btn>
            </v-tabs>
            <v-tabs-items
              v-model="rightTabs"
            >
              <v-tab-item
                v-if="checkPermission('contacts_messages', PERMISSIONS.CAN_VIEW)"
                key="contactlog"
                style="height: 100vh;max-height:calc(100vh - 241px);background:white;overflow:hidden;"
              >
                <ContactMessages
                  v-if="$vuetify.breakpoint.mdAndUp && !smsRequestSending"
                  :contact="contact"
                />
              </v-tab-item>
              <v-tab-item
                v-if="checkPermission('contacts_files', PERMISSIONS.CAN_VIEW)"
                key="files"
              >
                <ContactFiles
                  :contact.sync="contact"
                  :loading.sync="loading"
                  @reload="fetch()"
                />
              </v-tab-item>
            </v-tabs-items>
          </v-flex>
        </v-layout>
      </v-card>
    </v-expand-transition>
    <v-layout
      v-if="loading && !contact.id"
      align-center
      justify-center
      column
      fill-height
      style="height: 60vh;"
    >
      <div>
        <v-icon
          size="72"
        >
          person
        </v-icon>
      </div>
      <v-progress-circular
        indeterminate
        style="padding: 10px;"
      />
    </v-layout>    

    <v-dialog
      v-model="getLoginLink"
      width="600"
    >
      <v-card>
        <v-toolbar
          flat
          color="primary"
          dense
          dark
        >
          Get Login Link
          <v-spacer />
          <v-btn
            fab
            small
            flat
            @click="getLoginLink = false"
          >
            x
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-alert
            v-if="!linkObj"
            :value="true"
            type="warning"
          >
            <b>Warning</b>: This link should <u>only</u> be used by the contact. You should not use this link to log in and act as the contact or to view what the contact sees.<br /><br />
            This link must be used within 15 minutes, and can only be used once.
          </v-alert>
          <div
            v-else
          >
            <p>
              Click below to copy this link to your clipboard.
            </p>
            <v-textarea
              :value="linkObj.link"
              readonly
              hint="Copied to Clipboard!"
              style="max-width: 550px;"
              auto-grow
              @click="copyUrl()"
            />
          </div>
        </v-card-text>
        <v-card-actions
          v-if="!linkObj"
          style="padding-left: 20px; padding-right: 20px;"
        >
          <v-checkbox
            v-model="loginLinkAck"
            label="I understand, continue"
          />
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="getLoginLink = false"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="!loginLinkAck"
            color="blue"
            :dark="loginLinkAck"
            @click="getLogin()"
          >
            Generate Login
          </v-btn>
        </v-card-actions>
        <v-card-actions
          v-else
        >
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="getLoginLink = false;linkObj = null"
          >
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>  

    <v-dialog
      v-model="sendSMSRequest"
      width="600"
    >
      <v-card>
        <v-card-text>
          <b>
            SMS Compliance Notice
          </b><br /><br />
          <p>
            This will send a request to the contact to subscribe to SMS messages. The Contact must reply "START", "YES", "Y" to this message to receive SMS notifications about new messages in their account.<br /><br />
            You can only send this request once every 90 days. If the Contact does not reply to this message, they will not receive SMS messages from {{ app_name }}.<br /><br />
            The Contact can also explicitly subscribe to SMS messages by sending "START", "YES", or "Y" to the phone number <b>{{ domainInfo.text_number }}</b> from the phone number on their account.
          </p>
        </v-card-text>
        <v-card-actions
          style="padding-left: 20px; padding-right: 20px;"
        >
          <v-checkbox
            v-model="smsRequestAck"
            label="I understand, continue"
          />
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="sendSMSRequest = false"
          >
            Cancel
          </v-btn>
          <v-btn
            :disabled="smsRequestSending || !smsRequestAck"
            color="blue"
            :dark="smsRequestAck"
            @click="doSmsRequest()"
          >
            Send SMS Subscribe Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="sentLink"
      right top
      :timeout="6000"
    >
      <v-icon color="white">mail</v-icon>
      Invite Details have been sent!
    </v-snackbar>

    <ContactDialog
      v-if="editing"
      v-model="contact"
      @input="editing = false"
      @cancel="editing = false"
    />
  </div>
</template>

<script>
import api from '@/api'
import ContactDialog from '@/components/admin/contacts/ContactDialog'
import ContactFiles from './ContactFiles'
import ContactTemplates from './ContactTemplates'
import ContactMessages from './ContactMessages'
import CaseList from '@/components/admin/cases/CaseList.vue'
import ContactCase from '@/components/admin/contacts/ContactCase.vue'
import Editor from '@/components/admin/Editor'
import { mapGetters } from 'vuex'

export default {
  name: 'Contact',
  components: {
    ContactDialog,
    ContactFiles,
    ContactTemplates,
    ContactMessages,
    CaseList,
    ContactCase,
    Editor
  },
  data () {
    return {
      loading: false,
      search: '',
      contact: {},
      editing: false,
      sentLink: false,
      sendLink: false,
      notActivatedWarn: false,
      tagSearch: null,
      tagResults: [],
      tagSearching: false,
      tabs: null,
      rightTabs: null,
      getLoginLink: false,
      linkObj: null,
      loginLinkAck: false,
      downloadingCSV: false,
      sendSMSRequest: false,
      smsRequestAck: false,
      smsRequestSending: false
    }
  },
  created () {
    if (this.$route.params.id) {
      this.fetch()
    } else {
      this.$router.push({ name: 'ContactList' })
    }
  },
  beforeRouteUpdate (to, from, next) {
    this.$route.params.id = to.params.id
    if (to.name != 'ContactContactCase')
      this.fetch()
    next()
  },
  watch: {
    tagSearch () {
      if (this.tagSearch) {
        this.fetchTags()
      }
    },
  },
  computed: {
    domainInfo () {
      return this.getDomainInfo
    },
    canSendMagic () {
      return (this.contact.is_active && (this.contact.email !== null && this.contact.email !== ''))
    },
    bouncedErrorMessage () {
      if (this.contact.bounces > 0 || this.contact.suspended){
        if(this.contact.complaints > 0){
          return `Delivery of e-mails to '${this.contact.email}' has been suspended due to spam complaints`
        }
        if (this.contact.bounces >= 3){
          return `${this.app_name} is unable to send e-mails to '${this.contact.email}' due to repeated failures. Please update the e-mail to reactivate this contact's account.`
        }
        return `We have had ${this.contact.bounces == 1 ? "an e-mail" : "e-mails"} bounce from ${this.app_name} sent to '${this.contact.email}', we will continue to attempt to deliver e-mails to the contact.`
      }
    },
    showBadEmail () {
      if(this.contact.bounces){
        if(this.contact.bounces < 3) {
          return false
        }
        return true
      }
      return false
    },
    saving () {
      return this.contact.id && this.loading
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.userform.contact.get(this.$route.params.id)
        .then(contact => {
          this.documentTitle('Contact > ' + contact.full_name + ' (' + contact.cuid + ')')
          this.contact = contact
          this.contact.tags = this.resortTags(this.contact.tags)

          var lastViewedObject = JSON.parse(localStorage.getItem('user-last-viewed:' + this.$store.state.auth.id)) || []
          var thisFormObject = {
            type:'contact',
            id: this.contact.id,
            full_name: this.contact.full_name,
            cuid: this.contact.cuid,
            tags: this.contact.tags
          }
          if (lastViewedObject !== []) {
            if (lastViewedObject.some(e => e.id === this.contact.id)) {
              lastViewedObject = lastViewedObject.filter(obj => {
                return obj.id !== this.contact.id
              })
            }
            lastViewedObject.unshift(thisFormObject)
            while (lastViewedObject.length > 10) {
              lastViewedObject.pop()
            }
          } else {
            lastViewedObject.push(thisFormObject)
          }
          localStorage.setItem('user-last-viewed:' + this.$store.state.auth.id, JSON.stringify(lastViewedObject))
        })
      .finally(() => {
        this.loading = false
      })
    },
    getLogin () {
      if (this.contact.is_active && this.contact.email !== "") {
        api.userform.contact.getLogin(this.contact)
          .then(response => {
            this.linkObj = response
          })
      }
    },
    doSmsRequest () {
      this.smsRequestSending = true
      if (this.contact.is_active && this.contact.phone !== "") {
        api.userform.contact.smsInvite(this.contact)
          .then(response => {
            this.fetch()
          })
          .finally(() => {
            this.sendSMSRequest = false
            this.smsRequestSending = false
          })
      }
    },
    toggleContactActive () {
      this.loading = true
      this.contact.is_active = !this.contact.is_active
      api.userform.contact.save(this.contact)
        .then(contact => {
          this.loading = false
          this.contact = contact
        })
    },
    fetchTags () {
      this.tagSearching = true
      api.userform.contactTags.get(this.tagSearch)
        .then(tagResults => {
          this.tagResults = tagResults
          this.tagSearching = false
        })
    },
    updateTags (event) {
      this.tagSearch = ''
      var tagsCleaned = []
      this.contact.tags.forEach((t) => {
        tagsCleaned.push(t.trim())
      })
      const contactUpdate = {
        id: this.contact.id,
        tags: tagsCleaned
      }
      api.userform.contact.save(contactUpdate)
        .then(contact => {
          this.contact = contact
        })
    },
    unreadCount (items) {
      var count = 0
      if (items) items.forEach((item) => {
          if (!item.read) count++
      })
      return count
    },
    copyUrl() {
      const el = document.createElement('textarea');  
      el.value = this.linkObj.link;                                 
      el.setAttribute('readonly', '');                
      el.style.position = 'absolute';                     
      el.style.left = '-9999px';                      
      document.body.appendChild(el);                  
      const selected =  document.getSelection().rangeCount > 0  ? document.getSelection().getRangeAt(0) : false;                                    
      el.select();                                    
      document.execCommand('copy');                   
      document.body.removeChild(el);                  
      if (selected) {                                 
        document.getSelection().removeAllRanges();    
        document.getSelection().addRange(selected);   
      }
    },
    downloadCSV() {
      this.downloadingCSV = true
      api.bulkmail.mailsearch.downloadIndividualCSV(this.contact.id)
        .then(response => {
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `${this.contact.full_name}_Messages_Export.csv`
          link.click()
          this.downloadingCSV = false
        }) 
    }
  }
}
</script>

<style>

.v-window {
  overflow: auto !important;
}

.contact-header {
  width:100%;
  background: #ECEFF1;
  border-bottom: 1px solid #78909C;
}

.contact-header-container {
  position: relative;
  width: 100%;
  margin:auto;
  height: calc(100% - 25px);
  padding: 15px 20px 10px 20px;
}

.bounced {
  color:red;
  background: white;
  padding: 3px;
  border: 1px solid red;
}

.bounced span {
font-weight: 700;
}
</style>
